// PatientProfile.jsx
import React, { useState } from 'react';
import { Activity, AlertCircle, FileText, Package, Info, MapPin, Phone, Stethoscope, Pill } from 'lucide-react';
import { HomeOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useGetProfileDetailQuery } from '../../utils/apiSlice';
import HealthQuestionnaireModal from '../../components/HealthQuestionnaireModal';
import { useEffectiveUserData } from '../../hooks/useEffectiveUserData';

import './profile.scss';

const PatientProfile = () => {
    const [showQuestionnaire, setShowQuestionnaire] = useState(false);
    const navigate = useNavigate();

    const userData = useEffectiveUserData();
    const getStoredUser = () => {
        // Try localStorage
        const localData = localStorage.getItem("userData");
        if (localData) return JSON.parse(localData);

        // Fallback to sessionStorage
        const sessionData = sessionStorage.getItem("userData");
        if (sessionData) return JSON.parse(sessionData);

        // Return default if neither exists
        return {};
    }


    const storedUser = getStoredUser();
    const fullNameFromStorage = [
        storedUser?.firstName,
        storedUser?.middleName,
        storedUser?.lastName
    ]
        .filter(Boolean)
        .join(" ");


    const patientID = userData?.patientId
    const { data: profile = {}, isFetching: isProfileFetching } = useGetProfileDetailQuery(patientID, {
        skip: !patientID,
    });

    const formatName = (name: string) => {
        if (!name) return "";
        return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
    };

    /** Banner (name + avatar): same source as location — patient profile API only, not session/login fallback. */
    const profileBannerName = [
        formatName(profile.firstName || ""),
        formatName(profile.middleName || ""),
        formatName(profile.lastName || ""),
    ]
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();

    const bannerFullName =
        profileBannerName ||
        (isProfileFetching
            ? "Loading…"
            : fullNameFromStorage || "User");




    const bannerInitial = profileBannerName?.trim()
        ? profileBannerName.trim().charAt(0).toUpperCase()
        : isProfileFetching
            ? "…"
            : fullNameFromStorage
                ? fullNameFromStorage.charAt(0).toUpperCase()
                : "?";

    const formatDate = (date: any) => {
        if (!date) return "--";
        return new Date(date).toLocaleDateString("en-IN", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    return (
        <div className="content-wrapper">
            <div className="container-full table-padding">

                <div className="page-header">


                    <div className="breadcrumb">
                        <h3 className="page-title">Profile Setting</h3>
                        <span className="customize-breadcrumb">
                            <span className="home-icon" onClick={() => navigate("/dashboard")}><HomeOutlined /></span>
                            <span className="separator"> - </span>
                            <span className="current">Profile</span>
                        </span>

                    </div>
                </div>
                <div className="row g-4">
                    <div className="col-12">
                        <div className="content-card p-0 overflow-hidden mb-4 shadow-sm border-0">
                            <div className="profile-cover">
                                <button
                                    className="health-q-btn"
                                    onClick={() => { setShowQuestionnaire(true) }}
                                    title={patientID ? "Edit Health Questionnaire" : "Add Health Questionnaire"}
                                // disabled={!patientID}
                                // style={!patientID ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                                >
                                    <Stethoscope size={20} />
                                </button>
                            </div>

                            <div className="profile-avatar-wrapper d-flex align-items-end mb-4 px-4">
                                <div className="avatar-circle">{bannerInitial}</div>
                                <div className="ms-4 mb-2 nme">
                                    <h3 className="fw-bold mb-1 nm">{bannerFullName}</h3>
                                    <p className="text-muted mb-0">
                                        <MapPin size={14} className="me-1" />
                                        {profile.currentLocation || "--"}
                                    </p>
                                </div>
                            </div>

                            <div className="p-4 pt-0">
                                <h5 className="section-header">
                                    <Info size={20} className="me-2" />
                                    General Information
                                </h5>
                                <hr className="my-10"></hr>
                                <div className="row g-4">
                                    <div className="col-md-4">
                                        <div className="info-label">Member ID</div>
                                        <div className="info-value">{profile.memberId && '#'}{profile.memberId || userData?.memberId || "--"}</div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="info-label">Blood Group</div>
                                        <div className="info-value text-danger">
                                            {profile.bloodGroup || (userData?.bloodGroup == null ? '--' : '--') || "--"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="info-label">Gender</div>
                                        <div className="info-value">
                                            {formatName(profile.gender || profile.sexAssignedAtBirth || (userData?.gender == null ? '--' : '--') || "--")}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="info-label">Date of Birth</div>
                                        <div className="info-value">
                                            {(() => {
                                                const dob = profile?.dateOfBirth || userData?.dateOfBirth;

                                                const isValidDate =
                                                    dob && !isNaN(new Date(dob).getTime());

                                                return isValidDate ? formatDate(dob) : "--";
                                            })()}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="info-label">Height</div>
                                        <div className="info-value">
                                            {profile.heightInCm ? `${profile.heightInCm} cm` : "--"}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="info-label">Weight</div>
                                        <div className="info-value">
                                            {profile.weightInKg ? `${profile.weightInKg} kg` : "--"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                                    <div>
                                        <div className='p-2'>
                                            <h5 className="section-header">
                                                <Activity size={20} className="me-2" />
                                                Medical Information
                                            </h5>
                                            <hr className="my-10"></hr>
                                            <h5 className="section-header-inner">
                                                <AlertCircle size={20} className="me-2" />
                                                Active Allergies
                                            </h5>

                                            <div className="mb-4">
                                                {profile.allergies?.length > 0 ? (
                                                    profile.allergies.map((item: any) => (
                                                        <span key={item.id} className="med-tag allergy-alert">
                                                            {item.name}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <span>No allergies found</span>
                                                )}
                                            </div>

                                            <h5 className="section-header-inner">
                                                <FileText size={20} className="me-2" />
                                                Diagnosed Conditions
                                            </h5>
                                            <div className="mb-4">
                                                <span className="med-tag">Other bacterial intestinal infections</span>
                                                <span className="med-tag">Amebiasis</span>
                                            </div>

                                            <h5 className="section-header-inner">
                                                <Package size={20} className="me-2" />
                                                Medications List
                                            </h5>

                                            <div className="d-flex flex-wrap gap-2 mb-4">
                                                {profile.medications?.length > 0 ? (
                                                    profile.medications.map((med: any) => (
                                                        <span key={med.id} className="med-tag">
                                                            <i className="bi bi-capsule-pill me-2 text-primary"></i>
                                                            {med.name}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <p className="text-muted small">No medications listed.</p>
                                                )}
                                            </div>

                                            <h5 className="section-header-inner">
                                                <Pill size={20} className="me-2" />
                                                Suppliments
                                            </h5>

                                            <div className="d-flex flex-wrap gap-2 mb-4">
                                                {profile.supplements?.length > 0 ? (
                                                    profile.supplements.map((item: any) => (
                                                        <span key={item.id} className="med-tag">
                                                            <i className="bi bi-sparkles me-2 text-success"></i>
                                                            {item.name}
                                                        </span>
                                                    ))
                                                ) : (
                                                    <p className="text-muted small italic">No supplements listed.</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 col-sm-12 col-md-6 col-lg-6">
                                    <div>
                                        <div className='p-2'>
                                            <h5 className="section-header">
                                                <Phone size={20} className="me-2" />
                                                Emergency
                                            </h5>
                                            <hr className="my-10"></hr>

                                            <div className="row g-4">
                                                <div className="col-md-6">
                                                    <div className="p-3 rounded-4 ">
                                                        <div className="info-label">
                                                            Emergency Contact Name
                                                        </div>
                                                        <div className="info-value">{profile.emergencyContactName || "--"}</div>
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="p-3 rounded-4 ">
                                                        <div className="info-label">Relationship</div>
                                                        <div className="info-value">
                                                            {profile.emergencyRelationship || "--"}
                                                        </div>

                                                    </div>
                                                </div>

                                                <div className="col-12">
                                                    <div className="p-4 d-flex align-items-center justify-content-between  rounded-4">
                                                        <div>
                                                            <div className="info-label">Phone Number</div>
                                                            <div className="info-value">
                                                                {profile.emergencyPhoneNumber || "--"}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>



                        </div>
                    </div>


                </div>

            </div>
            <HealthQuestionnaireModal

                open={showQuestionnaire}
                onClose={() => setShowQuestionnaire(false)}
                patientId={patientID}
                userId={userData?.userId}
                initialData={profile}
                mode={'update'}
            />
        </div>
    );
};

export default PatientProfile;



